/*
 * @providesModule ModalNavigationController
 *
 *
 */

import LoadingIndicator from 'LoadingIndicator';
import {debounce} from 'lodash';
import React from 'react';
import Sizes from 'Sizes';

import {Modal, StyleSheet, View, Platform} from 'react-native';

export const SAFE_MODAL_CHANGE_TIME = 400;

const modalNavigationControllerInstances = new Map<string, ModalNavigationController>();

function getModalNavigationInstance(
  modalId: string = 'default'
): ModalNavigationController | undefined {
  return modalNavigationControllerInstances.get(modalId);
}

export type ModalProps = {
  /** The animation to use when the modal is shown; defaults to fade */
  animationType?: 'slide' | 'fade' | 'none';

  /**
   * @deprecated Whether the modal is shown over a transparent overlay; this
   * prop value is ignored and always set to true on the Modal component
   */
  transparent?: boolean | string;

  /**
   * Optional ID used for deduplication
   *
   * If a modal is shown with the same deduplicationID as a currently visible modal,
   * the modal will not be pushed onto the modal stack again
   *
   * If a modal is shown with the same deduplicationID as a modal in the stack,
   * the modal will be shown but removed from the stack, i.e. brought to the top
   * of the stack
   */
  deduplicationID?: string;

  /**
   * The modalBackHandler allows us to specify the functionality of the Android hardware back button.
   * If modalBackHandler returns true, the modal will close when the back button is pressed.
   */
  modalBackHandler?: () => boolean | void;
};

const DEFAULT_MODAL_PROPS: ModalProps = {animationType: 'fade', transparent: true};

/** The data required to display the modal */
interface ModalData {
  component: React.ReactNode;
  props?: ModalProps;
}

/**
 * The stack of modals shown by this controller
 * The currently visible modal is the first element in this array
 */
type ModalStack = Array<ModalData>;

type Props = {
  modalId?: string;
};

type State = {
  modalVisible: boolean;
  modals: ModalStack;
};

export default class ModalNavigationController extends React.Component<Props, State> {
  private modalBackHandler?: () => boolean | void;

  private loadingIndicatorActive = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      modalVisible: false,
      modals: [],
    };

    modalNavigationControllerInstances.set(props.modalId || 'default', this);
  }

  static isModalShown(modalId?: string): boolean {
    const modalNavigationControllerInstance = getModalNavigationInstance(modalId);
    return modalNavigationControllerInstance?.modalShown || false;
  }

  static pauseModalForDisplay(modalId?: string) {
    const modalNavigationControllerInstance = getModalNavigationInstance(modalId);
    modalNavigationControllerInstance?.pauseModalForDisplay();
  }

  private pauseModalForDisplay() {
    if (this.modalShown) {
      this.setState({modalVisible: false});
    }
  }

  static unpauseModalForDisplay(modalId?: string) {
    const modalNavigationControllerInstance = getModalNavigationInstance(modalId);
    modalNavigationControllerInstance?.unpauseModalForDisplay();
  }

  private unpauseModalForDisplay() {
    if (!this.state.modalVisible && this.modalCount) {
      this.setState({modalVisible: true});
    }
  }

  /**
   * Shows a modal; this is debounced to prevent repeatedly showing a modal in
   * a short time span
   *
   * @param modalComponent the component to render in the RN Modal
   * @param modalProps optional props used for configuring the modal
   */
  static showModal = debounce(
    (modalComponent: React.ReactNode, props?: ModalProps, modalId?: string) => {
      const modalNavigationControllerInstance = getModalNavigationInstance(modalId);
      modalNavigationControllerInstance?.showModal(modalComponent, props);
    },
    500,
    {leading: true, trailing: false}
  );

  private showModal(component: React.ReactNode, props = DEFAULT_MODAL_PROPS) {
    this.modalBackHandler = props?.modalBackHandler;

    const {deduplicationID} = props;

    // If we're showing a modal, check whether it's the same as the modal we
    // want to show; if so, we don't need to do anything since it's already shown
    if (
      this.modalShown &&
      deduplicationID &&
      this.currentModal?.props?.deduplicationID === deduplicationID
    ) {
      return;
    }

    const show = () => {
      // If the loading indicator is inactive, show it so that it blocks any
      // interactions as we're transitioning
      if (!this.loadingIndicatorActive) {
        LoadingIndicator.showInvisible();
        this.loadingIndicatorActive = true;
      }

      // Set a timeout to wait for the previous modal to hide before showing
      // the new one; this has no delay if there isn't a modal to dismiss
      setTimeout(() => {
        this.setState(
          (prevState) => ({
            modals: [
              {component, props},

              // If we have a deduplicationID, remove any modals with the same ID
              ...prevState.modals.filter(
                ({props}) => !deduplicationID || props?.deduplicationID !== deduplicationID
              ),
            ],
            modalVisible: true,
          }),
          () => {
            setTimeout(() => {
              LoadingIndicator.hideInvisible();
            }, this.modalTransitionTimeout);
          }
        );
      }, this.modalTransitionTimeout);
    };

    if (this.state.modalVisible) {
      this.setState({modalVisible: false}, show);
    } else {
      // If no modal is visible, we can immediately show
      show();
    }
  }

  /**
   * Shows multiple modals in a sequence
   *
   * The given modals are pushed onto the existing modal stack in order, i.e.
   * modals[0] will be shown first, then modals[1] once modals[0] is dismissed,
   * etc.
   *
   * If any of the modals have deduplicationIDs, previous instances of the
   * modals will be removed from the current stack
   */
  static showModals(modals: ModalStack) {
    const modalNavigationControllerInstance = getModalNavigationInstance();
    if (!modalNavigationControllerInstance || !modals.length) return;

    const {filteredModals, deduplicationIDs} = this.processNewModals(modals);
    const topModal = filteredModals[0];

    // If we only have one modal, show it directly
    if (filteredModals.length === 1) {
      modalNavigationControllerInstance.showModal(topModal?.component, topModal?.props);
      return;
    }

    // First hide the current modal so we can modify the stack after it's hidden;
    // otherwise, we'd see a different modal as it's animating out
    modalNavigationControllerInstance.setState({modalVisible: false}, () =>
      modalNavigationControllerInstance?.setState(
        (prevState) => ({
          modals: [
            // Put all but the top modal in our current stack since we'll be
            // manually showing the top modal
            ...filteredModals.slice(1),

            // Remove any modals that have an ID matching one of the new modals
            ...prevState.modals.filter(
              ({props}) =>
                !props?.deduplicationID || !deduplicationIDs.includes(props.deduplicationID)
            ),
          ],
        }),
        () => modalNavigationControllerInstance?.showModal(topModal?.component, topModal?.props)
      )
    );
  }

  static setModalBackHandler(handler: () => boolean, modalId?: string) {
    const modalNavigationControllerInstance = getModalNavigationInstance(modalId);
    if (modalNavigationControllerInstance) {
      modalNavigationControllerInstance.modalBackHandler = handler;
    }
  }

  static hideModal(onComplete?: () => void, modalId?: string, cleanModalStack = false) {
    const modalNavigationControllerInstance = getModalNavigationInstance(modalId);
    modalNavigationControllerInstance?.hideModal(onComplete, cleanModalStack);
  }

  private hideModal(onComplete?: () => void, cleanModalStack = false) {
    // Temporarily hide the modal stack while we remove the top modal
    this.setState({modalVisible: false}, () => {
      setTimeout(() => {
        this.setState(
          ({modals}) => {
            const remainingModals = cleanModalStack ? [] : modals.slice(1);
            return {
              modals: remainingModals,
              modalVisible: remainingModals.length > 0,
            };
          },
          () => {
            this.modalBackHandler = this.currentModal?.props?.modalBackHandler;

            // If active, hide the loading indicator once our stack is empty
            if (this.loadingIndicatorActive && !this.modalCount) {
              LoadingIndicator.hide();
              this.loadingIndicatorActive = false;
            }

            onComplete?.();
          }
        );
      }, this.modalTransitionTimeout);
    });
  }

  private handleModalCloseRequest() {
    // If the modal back handler is defined, call it and use it to determine
    // whether to close the modal; otherwise, close by default
    if (this.modalBackHandler?.() ?? true) {
      ModalNavigationController.hideModal();
    }
  }

  render(): React.ReactNode {
    const {currentModal, modalShown} = this;
    if (Platform.OS === 'web') {
      // Parity with the native branch below: wire onRequestClose so react-native-web's
      // <Modal> Escape listener routes through handleModalCloseRequest (which respects
      // modalBackHandler, so a modal that suppresses dismissal is unaffected). Before
      // this, web modals had no Escape at all and each had to install its own document
      // keydown listener. Native already had this via the Android hardware back button.
      return modalShown ? (
        <Modal
          transparent={true}
          id='react-native-modal'
          onRequestClose={this.handleModalCloseRequest.bind(this)}
        >
          <View style={styles.webModalOverlay}>{currentModal?.component}</View>
        </Modal>
      ) : null;
    }
    return (
      <Modal
        {...DEFAULT_MODAL_PROPS}
        {...currentModal?.props}
        transparent
        onRequestClose={this.handleModalCloseRequest.bind(this)}
        visible={modalShown}
      >
        <View
          style={[
            StyleSheet.absoluteFill,
            {maxWidth: Sizes.screenWidth, maxHeight: Sizes.screenHeight},
          ]}
        >
          {currentModal?.component}
        </View>
      </Modal>
    );
  }

  /** Returns the data for the modal that should be shown */
  private get currentModal(): ModalData | undefined {
    return this.state.modals[0];
  }

  /** Returns whether a modal is currently being shown */
  private get modalShown(): boolean {
    return this.state.modalVisible && !!this.currentModal;
  }

  /** Returns the number of modals currently on the stack */
  private get modalCount(): number {
    return this.state.modals.length;
  }

  /**
   * Returns the amount of time required to transition between modals
   *
   * If the modal stack is empty, this is 0 since we don't need to wait for the
   * previous modal to animate out before showing the new one
   */
  private get modalTransitionTimeout(): number {
    return this.modalCount ? SAFE_MODAL_CHANGE_TIME : 0;
  }

  /**
   * Helper method used to process a set of modals that will be pushed onto the
   * modal stack
   *
   * This method checks that the new modal set doesn't include any duplicates
   * and returns only modals with unique or undefined deduplicationIDs
   */
  private static processNewModals(modals: ModalStack): {
    filteredModals: ModalStack;
    deduplicationIDs: string[];
  } {
    const filteredModals: ModalStack = [];
    const deduplicationIDs: string[] = [];
    for (const modal of modals) {
      const deduplicationID = modal.props?.deduplicationID;
      if (!deduplicationID) {
        filteredModals.push(modal);
        continue;
      }

      // If we already have a modal with the same deduplicationID, ignore this modal
      if (deduplicationIDs.includes(deduplicationID)) {
        continue;
      }

      deduplicationIDs.push(deduplicationID);
      filteredModals.push(modal);
    }

    return {filteredModals, deduplicationIDs};
  }
}

const styles = StyleSheet.create({
  webModalOverlay: {
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
});
